import React, { useContext, Suspense, useMemo, useRef, useEffect, useState } from "react";
import { useAuth } from "../../utils/hooks/useAuth";
import Button from "../../components/ui/button/Button";
import { routeUrls } from "../../config/routeUrls";
import { Link, useNavigate } from "react-router-dom";
import imageServicesBag from "../../assets/images/icons/services_bag.svg";
import { ActionSidebarsContext } from "../../context/actionSidebarsContext";
import { IActionSidebarsContext } from "../../types/actionSidebars";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import imageFleetRoute from "../../assets/images/icons/fleet-route.svg";
import imageDocumentsDashboard from "../../assets/images/icons/documents-dashboard.svg";
import LinkButton from "../../components/ui/linkButton/LinkButton";
import { useTranslation } from "react-i18next";
import { RoutesDashboard } from "../../types/routes";
import { AccountStatusContext } from "../../context/accountStatusContext";
import { IAccountStatusContext } from "../../types/accountStatus";
import WidgetTrips from "../../components/widgets/WidgetTrips";
import ServicesWidget from "../../components/widgets/ServicesWidget";
import ContractsWidget from "../../components/widgets/ContractsWidget";
import DocumentsWidget from "../../components/widgets/DocumentsWidget";
import { BusinessProfile } from "../../types/profile";
import EamWidget from "../fleetAndRoute/components/EamWidget";
import { useWelcomePopup } from "../../hooks/useWelcomePopup";
import WelcomePopOver from "../../components/WelcomePopOver/WelcomePopOver";
import TokenDebugInfo from "../../components/TokenDebugInfo";
import { useBusinessProfile } from "../../utils/hooks/useBusinessProfile";
import Skeleton from "@mui/material/Skeleton";
import { countriesService } from "../../api/countriesService/countriesService";
import { fleetService } from "../../api/fleetService/fleetService";
import { profileService } from "../../api/profileService/profileService";
import { rolesService } from "../../api/rolesService/rolesService";
import { routesService } from "../../api/routesService/routesService";
import { servicesService } from "../../api/servicesService/servicesService";
import { IsAuthorized } from "../../utils/helpers/auth";
import { ServicesContext, IServicesContext } from "../../context/servicesContext";
import { FleetContext } from "../../context/fleetContext";
import { IFleetContext } from "../../types/fleet";

const Map = React.lazy(() => import("../../components/map/Map"));
const FleetGraphs = React.lazy(
  () => import("../../components/fleetGraphs/FleetGraphs")
);

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  // BusinessProfile viene caricato dal BusinessProfileContext, non più dalle chiamate API locali
  const { businessProfile } = useBusinessProfile();
  
  // State variables for API responses
  const [servicesApi, setServicesApi] = useState<any>({
    mainServices: [],
    additionalServices: []
  });
  const [countriesApi, setCountriesApi] = useState<any[]>([]);
  // RIMOSSO: servicesInfoApi - ora gestito da useSidebarDataLoader
  const [routesApi, setRoutesApi] = useState<any>({
    routes: []
  });
  const [vehiclesApi, setVehiclesApi] = useState<any[]>([]);
  const [engineTypeApi, setEngineTypeApi] = useState<any[]>([]);
  const [emissionClassApi, setEmissionClassApi] = useState<any[]>([]);
  const [vehiclesInfoApi, setVehiclesInfoApi] = useState<any[]>([]);
  const [isIntermodalApi, setIsIntermodalApi] = useState<boolean>(false);
  const [lastUpdateApi, setLastUpdateApi] = useState<any>(null);
  const [rolesApi, setRolesApi] = useState<any>({
    roles: []
  });
  // RIMOSSO: businessProfileApi - uso il businessProfile dal context invece

  const {
    setSidebarAddServiceOpen,
    setSidebarModifyFleetOpen,
    setSidebarAddRouteOpen,
  } = useContext(ActionSidebarsContext) as IActionSidebarsContext;

  const { setServices } = useContext(ServicesContext) as IServicesContext;
  const { setVehicles, setEmissionClass, setEngineType, setVehiclesInfo, setIsIntermodal, setLastUpdate } = useContext(FleetContext) as IFleetContext;

  const groupRoutes: RoutesDashboard = useMemo(() => {
    if (routesApi && routesApi.routes && routesApi.routes.length > 0) {
      const routesArray: string[] = [];
      routesApi.routes.forEach((route: any) => {
        if (!routesArray.includes(route.countryFrom.isoCode)) {
          routesArray.push(route.countryFrom.isoCode);
        }

        if (!routesArray.includes(route.countryTo.isoCode)) {
          routesArray.push(route.countryTo.isoCode);
        }
      });

      return { countries: routesArray };
    }

    return { countries: [] };
  }, [routesApi?.routes, routesApi]);



  // ! If user has partial access, redirect to contracts
  const navigate = useNavigate();
  const { userHasPartialAccess } = useContext(
    AccountStatusContext
  ) as IAccountStatusContext;
  const canGo = useRef(true);
  useEffect(() => {
    if (canGo.current) {
      canGo.current = false;

      if (userHasPartialAccess) {
        navigate(routeUrls.contracts);
      }
    }
  }, []);

  // BATCH 1: Core services and business data (4 chiamate)
  useEffect(() => {
    if (IsAuthorized()) {
      console.log('Dashboard: Starting BATCH 1 - Core services and business data');
      
      // Controlli di stato per evitare chiamate duplicate
      const hasServicesData = servicesApi.mainServices.length > 0 || servicesApi.additionalServices.length > 0;
      const hasCountriesData = countriesApi.length > 0;
      
      // Services
      if (!hasServicesData) {
        console.log('Dashboard: Fetching services data');
        servicesService.services().then((servicesRes: any) => {
          if (servicesRes && servicesRes.success) {
            console.log('Dashboard: Services data received:', servicesRes.responseBody);
            setServicesApi(servicesRes.responseBody);
            setServices(servicesRes.responseBody);
          }
        }).catch((error) => {
          console.error('Dashboard: Services API error:', error);
        });
      } else {
        console.log('Dashboard: Skipping services - data already available');
      }

      // Services Info - RIMOSSO: gestito completamente da useSidebarDataLoader
      console.log('Dashboard: Skipping servicesInfo - handled by useSidebarDataLoader');

      // Countries
      if (!hasCountriesData) {
        console.log('Dashboard: Fetching countries data');
        countriesService.countries().then((countriesRes: any) => {
          if (countriesRes && countriesRes.success) {
            console.log('Dashboard: Countries data received');
            setCountriesApi(countriesRes.responseBody);
          }
        }).catch((error) => {
          console.error('Dashboard: Countries API error:', error);
        });
      } else {
        console.log('Dashboard: Skipping countries - data already available');
      }
    }
  }, [setServices]); // Rimuovo tutte le dipendenze del cache system

  // BATCH 2: Fleet, routes and roles data (4 chiamate)
  useEffect(() => {
    if (IsAuthorized()) {
      console.log('Dashboard: Starting BATCH 2 - Fleet, routes and roles data');
      
      // Routes
      const routesPromise = routesService.getRoutes().then((routesRes: any) => {
        if (routesRes && routesRes.success) {
          console.log('Dashboard: Routes data received');
          setRoutesApi(routesRes.responseBody);
        }
      }).catch((error) => {
        console.error('Dashboard: Routes API error:', error);
      });

      // Fleet data
      const fleetPromise = fleetService.getFleet().then((fleetRes: any) => {
        if (fleetRes && fleetRes.success) {
          console.log('Dashboard: Fleet data received:', fleetRes.responseBody);
          setVehiclesApi(fleetRes.responseBody.fleet || []);
          setEngineTypeApi(fleetRes.responseBody.engineType || []);
          setEmissionClassApi(fleetRes.responseBody.emissionClass || []);
          setLastUpdateApi(fleetRes.responseBody.lastUpdate || null);
          // Salva i dati nel context per la sidebar
          setVehicles(fleetRes.responseBody.fleet || []);
          setEngineType(fleetRes.responseBody.engineType || []);
          setEmissionClass(fleetRes.responseBody.emissionClass || []);
          setLastUpdate(fleetRes.responseBody.lastUpdate || null);
        }
      }).catch((error) => {
        console.error('Dashboard: Fleet API error:', error);
      });

      // FleetInfo data (related to fleet)
      const fleetInfoPromise = fleetService.getFleetInfo().then((fleetInfoRes: any) => {
        if (fleetInfoRes && fleetInfoRes.success) {
          console.log('Dashboard: FleetInfo data received');
          setVehiclesInfoApi(fleetInfoRes.responseBody.vehicles || []);
          setIsIntermodalApi(fleetInfoRes.responseBody.isIntermodal || false);
          // Salva i dati nel context per la sidebar
          setVehiclesInfo(fleetInfoRes.responseBody.vehicles || []);
          setIsIntermodal(fleetInfoRes.responseBody.isIntermodal || false);
        }
      }).catch((error) => {
        console.error('Dashboard: FleetInfo API error:', error);
      });

      // Roles
      const rolesPromise = rolesService.getRoles().then((rolesRes: any) => {
        if (rolesRes && rolesRes.success) {
          console.log('Dashboard: Roles data received');
          setRolesApi(rolesRes.responseBody);
        }
      }).catch((error) => {
        console.error('Dashboard: Roles API error:', error);
      });

      // Esegui tutte le chiamate in parallelo senza attendere il completamento
      // Le chiamate vengono eseguite indipendentemente
    }
  }, [setVehicles, setEngineType, setEmissionClass, setLastUpdate, setVehiclesInfo, setIsIntermodal]);

  // RIMOSSO: Gestisci il merge dei servizi quando entrambi i dati sono disponibili
  // Ora servicesInfo viene gestito completamente da useSidebarDataLoader

  const { showWelcome, anchorEl, handleCloseWelcome } = useWelcomePopup();

  return (
    <>
      <div className="p-6 md:p-8">
        <h1 className="mb-1 text-[32px] font-bold leading-[37.5px]">
          {t("screens.dashboard.welcomeGreeting")} {user?.name}
        </h1>
        <h3 className="text-lg">{t("screens.dashboard.subHeading")}:</h3>
      </div>

      {/* WelcomePopOver skeleton */}
      {!businessProfile ? (
        <Skeleton variant="rectangular" width={320} height={80} sx={{ borderRadius: 2, marginBottom: 2 }} />
      ) : (
        <WelcomePopOver
          open={showWelcome}
          onClose={handleCloseWelcome}
          anchorEl={anchorEl}
        />
      )}

      <ResponsiveMasonry
        columnsCountBreakPoints={{ 0: 1, 1024: 2 }}
        className="pb-0 md:pb-8"
      >
        <Masonry
          gutter="24px"
          className="py-8 px-6 md:py-0 md:px-8 bg-lightGrey md:bg-white"
        >
          {/* EamWidget skeleton */}
          {!businessProfile ? (
            <Skeleton variant="rectangular" width="100%" height={140} sx={{ borderRadius: 2, marginBottom: 2 }} />
          ) : (
            <EamWidget businessProfile={businessProfile}/>
          )}

          {/* TRIPS skeleton */}
          {businessProfile && businessProfile.isagency === true ? (
            !businessProfile ? (
              <Skeleton variant="rectangular" width="100%" height={180} sx={{ borderRadius: 2, marginBottom: 2 }} />
            ) : (
              <WidgetTrips businessProfile={businessProfile} />
            )
          ) : null}

          {/* CONTRACTS (ha già skeleton) */}
          <ContractsWidget />

          {/* FLEET skeleton */}
          <div className="mb-[56px] md:mb-0 md:bg-lightGrey md:p-6 md:rounded card-shadow">
            <div className="sm:flex sm:justify-between sm:items-center sm:mb-6">
              <h2 className="font-light text-[24px] mb-4 sm:mb-0 leading-[38px]">
                {t("screens.dashboard.widgets.fleet.heading")}
              </h2>

              {isIntermodalApi === false && (
                <Button
                  className="mb-6 sm:mb-0"
                  callback={() => setSidebarModifyFleetOpen(true)}
                >
                  <div className="flex items-center">
                    <span className="mr-[12px]">
                      {t("common.buttonActions.fleet.modifyFleet")}
                    </span>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10.8419 8.81317L7.61792 5.58922L8.08915 5.118L12.9712 10.0001L8.08915 14.8821L7.61792 14.4109L10.8419 11.1869L11.6954 10.3334H10.4883H3.83331V9.66672H10.4883H11.6954L10.8419 8.81317ZM15.5 5.50006H16.1666V14.5001H15.5V5.50006Z"
                        fill="white"
                        stroke="white"
                      />
                    </svg>
                  </div>
                </Button>
              )}
            </div>

            <div className="grid grid-cols-12 gap-6">
              {!vehiclesApi || vehiclesApi.length === 0 ? (
                <Skeleton variant="rectangular" width="100%" height={180} sx={{ borderRadius: 2 }} />
              ) : (
                <Suspense fallback={<Skeleton variant="rectangular" width="100%" height={180} sx={{ borderRadius: 2 }} />}>
                  <FleetGraphs 
                    vehicles={vehiclesApi}
                    emissionClass={emissionClassApi}
                    engineType={engineTypeApi}
                  />
                </Suspense>
              )}
            </div>

            <div className="mt-6">
              <Link
                className="inline-flex items-center"
                to={routeUrls.fleetAndRoute}
              >
                <img src={imageFleetRoute} alt="services_icon_bag" />
                <div className="uppercase text-sm leading-[17px] underline text-red font-semibold ml-[8px] mt-[2px]">
                  {t("screens.dashboard.widgets.fleet.link")}
                </div>
              </Link>
            </div>
          </div>

          {/* DOCUMENTS (ha già skeleton) */}
          <DocumentsWidget />

          {/* ROUTES skeleton */}
          <div className="mb-[56px] md:mb-0 md:bg-lightGrey md:rounded card-shadow">
            <div className="md:p-6 md:pb-0 sm:flex sm:justify-between sm:items-center sm:mb-6">
              <h2 className="font-light text-[24px] mb-4 sm:mb-0 leading-[38px]">
                {t("screens.dashboard.widgets.routes.heading")}
              </h2>
              <Button
                className="mb-6 sm:mb-0"
                callback={() => setSidebarAddRouteOpen(true)}
              >
                <div className="flex items-center">
                  <span className="mr-[12px]">
                    {t("common.buttonActions.routes.addRoutes")}
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="w-[18px] h-[18px]"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 6v12m6-6H6"
                    />
                  </svg>
                </div>
              </Button>
            </div>

            <div className="md:h-auto -mx-6 md:mx-0 overflow-hidden">
              {!routesApi || !routesApi.routes || routesApi.routes.length === 0 ? (
                <Skeleton variant="rectangular" width="100%" height={180} sx={{ borderRadius: 2 }} />
              ) : (
                <Suspense fallback={<Skeleton variant="rectangular" width="100%" height={180} sx={{ borderRadius: 2 }} />}>
                  <Map routes={groupRoutes} />
                </Suspense>
              )}
            </div>

            <div className="pt-6 md:p-6">
              <Link
                className="inline-flex items-center"
                to={routeUrls.fleetAndRoute + "?tab=routes"}
              >
                <img src={imageFleetRoute} alt="services_icon_bag" />
                <div className="uppercase text-sm leading-[17px] underline text-red font-semibold ml-[8px] mt-[2px]">
                  {t("screens.dashboard.widgets.routes.link")}
                </div>
              </Link>
            </div>
          </div>
          {/* SERVICES (ha già skeleton) */}
          <ServicesWidget />
        </Masonry>
      </ResponsiveMasonry>
      {/* Componente di debug per le informazioni del token */}
      {/*<TokenDebugInfo />*/}
    </>
  );
};

export default Dashboard;
